import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { AnimatePresence } from "framer-motion";
import { EASE } from "./Reveal";

const WORDS = ["ALGORITHMIC", "STRUCTURED", "TRANSPARENT", "RISK-AWARE"];
const STREAMS = [
  { label: "SIGNAL", w: 72 },
  { label: "RISK", w: 46 },
  { label: "EXEC", w: 58 },
];

function Ring({ size, cls, reverse = false }) {
  const r = size / 2 - 1;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ${
        reverse ? "animate-spin-reverse" : "animate-spin-slower"
      }`}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="rgba(255,193,7,0.22)"
        strokeWidth="1"
        strokeDasharray="2 10"
      />
      <circle
        cx={size / 2}
        cy={r + 1}
        r="3"
        fill="#FFC107"
        style={{ filter: "drop-shadow(0 0 6px rgba(255,193,7,0.9))" }}
      />
    </svg>
  );
}

export default function HeroVisual() {
  const wrapRef = useRef(null);
  const [wordIdx, setWordIdx] = useState(0);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotateY = useTransform(sx, [-0.5, 0.5], [-9, 9]);
  const rotateX = useTransform(sy, [-0.5, 0.5], [8, -8]);

  useEffect(() => {
    const t = setInterval(() => setWordIdx((i) => (i + 1) % WORDS.length), 2600);
    return () => clearInterval(t);
  }, []);

  const onMouseMove = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <div
      ref={wrapRef}
      onMouseMove={onMouseMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      data-testid="hero-visual"
      className="relative mx-auto w-full max-w-[520px] aspect-square select-none"
      style={{ perspective: 1000 }}
    >
      {/* orbit rings */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
        className="absolute inset-0"
      >
        <Ring size={520} />
        <Ring size={400} reverse />
        <Ring size={280} />
      </motion.div>

      {/* 3D-tilting core */}
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="absolute inset-0 flex items-center justify-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.25, ease: EASE }}
          className="glass-gold glow-gold rounded-3xl p-8 w-[270px] sm:w-[300px]"
        >
          <div className="flex flex-col items-center text-center gap-4">
            <img
              src="/kalki-shield.png"
              alt="Kalki AI Fund gold shield emblem"
              className="h-20 w-20 object-contain"
            />
            <div>
              <p className="font-brand font-bold text-2xl tracking-[0.2em] text-logo-gold">
                KALKI <span className="text-logo-silver font-mono font-medium">AI</span>
              </p>
              <p className="font-mono text-[9px] tracking-[0.5em] text-zinc-500 mt-1">FUND</p>
            </div>
            <div className="h-8 flex items-center justify-center overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.p
                  key={wordIdx}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.45, ease: EASE }}
                  className="font-mono text-[11px] tracking-[0.32em] text-gold-300"
                >
                  {WORDS[wordIdx]}
                </motion.p>
              </AnimatePresence>
            </div>
            <div className="w-full flex flex-col gap-2.5 pt-2" aria-hidden="true">
              {STREAMS.map((s, i) => (
                <div key={s.label} className="flex items-center gap-3">
                  <span className="font-mono text-[9px] text-zinc-500 w-12 text-left">
                    {s.label}
                  </span>
                  <div className="flex-1 h-[3px] rounded-full bg-white/[0.06] overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-gold-500/60 to-gold-300"
                      initial={{ width: "8%" }}
                      animate={{ width: `${s.w}%` }}
                      transition={{ duration: 1.6, delay: 1 + i * 0.25, ease: EASE }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* floating model chips */}
      {[
        { label: "NON-CUSTODIAL", cls: "-top-2 -left-4 sm:-left-10", delay: "0s" },
        { label: "CUSTODIAL", cls: "top-1/3 -right-3 sm:-right-12", delay: "0.9s" },
        { label: "AUDITED & VERIFIED", cls: "-bottom-3 left-4 sm:left-0", delay: "1.7s" },
      ].map((chip) => (
        <motion.div
          key={chip.label}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, -9, 0] }}
          transition={{
            opacity: { duration: 0.8, delay: 1.4 },
            y: { duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: chip.delay },
          }}
          className={`absolute ${chip.cls} px-3 py-1.5 rounded-full glass border-gold-400/25`}
        >
          <span className="font-mono text-[9px] tracking-[0.22em] text-gold-300">{chip.label}</span>
        </motion.div>
      ))}
    </div>
  );
}